import type { PuzzleAct } from '@il-colpo/engine';

/**
 * The sound effects (ART_BIBLE §11.3). Each key names one sound the synthesiser knows how to
 * make; the manifest lists them all as procedural assets, so a recording can replace any of
 * them without touching the game.
 */
export const SFX_KEYS = [
  // Interface
  'ui.hover',
  'ui.click',
  'ui.confirm',
  'ui.error',
  'ui.toggle',
  // Puzzles
  'pin.click',
  'pin.fall',
  'safe.tick',
  'wire.snip',
  'relay',
  'mirror.click',
  'laser.zap',
  'camera.servo',
  'keys.type',
  'step',
  'block.slide',
  'vault.key',
  'vault.thunk',
  // The heist
  'stamp',
  'door',
  'coins',
  'paper',
  'siren',
  'ping',
  'phrase.in',
  'tick',
  'engine',
  // Stingers
  'stinger.error',
  'stinger.success',
  'stinger.twist',
  'stinger.fail',
  'stinger.win',
] as const;

export type SfxKey = (typeof SFX_KEYS)[number];

export type Bus = 'sfx' | 'ui' | 'music';

/** Which mixer bus each effect plays on. The interface has its own, under the effects slider. */
export function busOf(key: SfxKey): Bus {
  return key.startsWith('ui.') ? 'ui' : key.startsWith('stinger.') ? 'music' : 'sfx';
}

/** The sound of the player's own hands on a puzzle, the moment they act (before the result). */
export function sfxForAct(act: PuzzleAct): SfxKey {
  switch (act.kind) {
    case 'lock.set':
      return 'pin.click';
    case 'safe.try':
      return 'safe.tick';
    case 'panel.act':
      return act.act === 'cut' ? 'wire.snip' : 'relay';
    case 'cameras.rotate':
      return 'camera.servo';
    case 'cameras.verify':
      return 'ui.confirm';
    case 'laser.flip':
      return 'mirror.click';
    case 'laser.fire':
      return 'laser.zap';
    case 'password.try':
      return 'keys.type';
    case 'corridor.move':
      return 'step';
    case 'corridor.coin':
      return 'coins';
    case 'corridor.petard':
      return 'stinger.twist';
    case 'escape.move':
      return 'block.slide';
    case 'vault.set':
      return 'vault.key';
    case 'vault.confirm':
    case 'vault.unconfirm':
      return 'vault.thunk';
  }
}

export interface SoundEvent {
  type: string;
  data: Record<string, unknown>;
}

/**
 * What the table hears when something happens to the team. A small slip in the alarm is a
 * stinger, a big one (10 points or more) brings the siren with it.
 */
export function sfxForEvent(ev: SoundEvent): SfxKey[] {
  switch (ev.type) {
    case 'puzzle.solved':
      return ['stinger.success'];
    case 'room.cleared':
      return ['door'];
    case 'plan.approved':
      return ['stamp'];
    case 'plan.rejected':
      return ['stinger.error'];
    case 'alarm': {
      const delta = Number(ev.data['delta'] ?? 0);
      if (delta <= 0) return [];
      return delta >= 100 ? ['stinger.error', 'siren'] : ['stinger.error'];
    }
    case 'alarm.policeAlerted':
    case 'escape.reinforcement':
      return ['siren'];
    case 'escape.started':
      return ['engine', 'stinger.twist'];
    case 'heist.completed':
      return ['stinger.win', 'coins'];
    case 'heist.failed':
      return ['stinger.fail'];
    case 'lock.fall':
      return ['pin.fall'];
    case 'chat':
      return ['phrase.in'];
    case 'ping':
      return ['ping'];
    case 'split.result':
      return ev.data['approved'] ? ['coins'] : ['ui.error'];
    case 'accusation.result':
      return ['paper'];
    case 'notice':
    case 'clue.glitch':
      return ['stinger.twist'];
    case 'gear.used':
    case 'power.planB':
    case 'power.loop':
      return ['relay'];
    default:
      return [];
  }
}

/** A short buzz on touch screens (ART_BIBLE §11.4), in milliseconds; empty for none. */
export function hapticFor(key: SfxKey): readonly number[] {
  switch (key) {
    case 'pin.click':
    case 'safe.tick':
    case 'vault.key':
    case 'mirror.click':
      return [8];
    case 'stinger.error':
    case 'ui.error':
      return [40, 30, 60];
    case 'stinger.success':
    case 'stinger.win':
      return [20, 40, 20];
    case 'stamp':
      return [25];
    case 'siren':
      return [30, 60, 30, 60];
    default:
      return [];
  }
}
